import React, {
  Context,
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {once} from 'lodash';
import {receiveNotificationSettings} from '../../../shared/account-settings/actions/notification-settings';
import {useDispatch} from 'react-redux';
import {changesSaved} from '../../../shared/actions/notifications';
import {setNavigationAnnouncement} from 'shared/actions/accessibility-announcement';

export interface SharedAccountSettings {
  email: string;
  mobile: string;
  notificationSettings: object;
}

export interface AccountSettingsContextProps<Type extends SharedAccountSettings> {
  saveSettings: (settings: Partial<Type>, previousSettings?: Type | null) => Promise<Type>;
  fetchAccountSettings: () => Promise<Type>;
}

interface AccountSettingsContextValue<Type extends SharedAccountSettings> {
  saveSettings: (settings: Partial<Type>, previousSettings?: Type | null) => Promise<Type>;
  setSettings: (settings: Type) => void;
  settings: Type;
  setNavigationAnnouncement: (message: string) => void;
  isSubmitting: boolean;
}

const createAccountSettingsContext = once(
  <Type extends SharedAccountSettings>(): Context<AccountSettingsContextValue<Type>> =>
    createContext({} as AccountSettingsContextValue<Type>)
);
export const useAccountSettingsContext = <
  Type extends SharedAccountSettings,
>(): AccountSettingsContextValue<Type> => useContext(createAccountSettingsContext<Type>());

let previouslyRetrievedSettings: SharedAccountSettings | null = null;

export const AccountSettingsProvider = <Type extends SharedAccountSettings>({
  children,
  fetchAccountSettings,
  saveSettings: providedSaveSettings,
}: PropsWithChildren<AccountSettingsContextProps<Type>>): ReactElement | null => {
  const AccountSettingsContext = createAccountSettingsContext<Type>();
  const [settings, setSettings] = useState<Type | null>(previouslyRetrievedSettings as Type | null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const connectedSetNavigationAnnouncement = useCallback(
    (message: string) => dispatch(setNavigationAnnouncement(message)),
    [dispatch]
  );

  useEffect(() => {
    fetchAccountSettings().then((retrievedSettings: Type) => {
      dispatch(receiveNotificationSettings(retrievedSettings.notificationSettings));
      setSettings(retrievedSettings);
      previouslyRetrievedSettings = retrievedSettings;
    });
  }, []);

  const saveSettings = async (newSettings: Partial<Type>): Promise<Type> => {
    const completeSettings = {...settings, ...newSettings} as Type;
    setSettings(completeSettings);
    setIsSubmitting(true);
    const updatedSettings = await providedSaveSettings(newSettings, settings);
    const finalSettings = {...settings, ...updatedSettings} as Type;
    setSettings(finalSettings);
    dispatch(changesSaved());
    previouslyRetrievedSettings = finalSettings;
    setIsSubmitting(false);
    return updatedSettings;
  };

  if (!settings) {
    return null;
  }

  return (
    <AccountSettingsContext.Provider
      value={{
        settings,
        setSettings,
        saveSettings,
        setNavigationAnnouncement: connectedSetNavigationAnnouncement,
        isSubmitting,
      }}
    >
      {children}
    </AccountSettingsContext.Provider>
  );
};
